import { DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';

/**
 * Behaviour the shared search overlay adds on top of `dialog-component`:
 * - positions the overlay beneath the header as it currently sits in the viewport,
 * - closes when the press and release both land outside the field, close control and results panel,
 * - makes sure no other overlay stays open behind it.
 *
 * Opening, Escape, focus handling, scroll locking and scroll restoration stay with `dialog-component`
 * and the native modal dialog.
 */

/** Parts of the overlay that never count as the backdrop. */
const INTERACTIVE_SELECTOR = '.search-overlay__field, .search-overlay__close, .search-overlay__panel';

const host = /** @type {(HTMLElement & { closeDialog?: () => void }) | null} */ (
  document.getElementById('search-modal')
);
const dialog = host?.querySelector('dialog');

if (host && dialog instanceof HTMLDialogElement) {
  /** @type {AbortController | null} */
  let openController = null;
  let pressStartedOutside = false;

  /**
   * @param {EventTarget | null} target
   * @returns {boolean} Whether the target is the backdrop, the band or any other non-interactive part.
   */
  const isOutside = (target) => target instanceof Element && !target.closest(INTERACTIVE_SELECTOR);

  const updateTop = () => {
    const header = document.querySelector('#header-component');
    const bottom = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
    dialog.style.setProperty('--search-overlay-header-bottom', `${bottom}px`);
  };

  /** Closes the mobile menu and any other modal dialog so search is the only active overlay. */
  const closeOtherOverlays = () => {
    const menuDrawer = /** @type {(HTMLElement & { close?: () => void }) | null} */ (
      document.querySelector('header-drawer')
    );
    if (menuDrawer?.querySelector('details[open]')) menuDrawer.close?.();

    for (const other of document.querySelectorAll('dialog[open]')) {
      if (other === dialog) continue;

      const owner = /** @type {(HTMLElement & { close?: () => void, closeDialog?: () => void }) | null} */ (
        other.closest('theme-drawer, dialog-component')
      );
      if (typeof owner?.closeDialog === 'function') owner.closeDialog();
      else if (typeof owner?.close === 'function') owner.close();
    }
  };

  host.addEventListener(DialogOpenEvent.eventName, () => {
    openController?.abort();
    openController = new AbortController();
    const { signal } = openController;

    pressStartedOutside = false;
    updateTop();
    closeOtherOverlays();

    window.addEventListener('resize', updateTop, { signal });

    // A press that starts inside the field (selecting text, for example) and ends on the backdrop must not close.
    dialog.addEventListener(
      'pointerdown',
      (event) => {
        pressStartedOutside = isOutside(event.target);
      },
      { signal }
    );

    dialog.addEventListener(
      'click',
      (event) => {
        const shouldClose = pressStartedOutside && isOutside(event.target);
        pressStartedOutside = false;
        if (!shouldClose) return;

        event.preventDefault();
        host.closeDialog?.();
      },
      { signal }
    );
  });

  host.addEventListener(DialogCloseEvent.eventName, () => {
    openController?.abort();
    openController = null;
  });
}
